import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// DocumentPreview.tsx -- modal overlay that renders a document via the
// BFF's content/private route. Documents are private (no public S3
// URLs), so we always go through the membership-gated proxy.
//
// Workbench D.6 follow-up: when the user clicks a search hit OR
// double-clicks a desktop file icon, the URL becomes
// /p/:projectId/d/:documentId. ProjectDesktop's url-sync effect
// triggers openPreview(documentId) and renders this component.
//
// Rendering strategy: branch on content-type.
//   - images  -> an interactive <img> inside react-zoom-pan-pinch
//                (fit-to-frame, wheel/pinch zoom, pan, reset -- the
//                PDF-viewer-like experience for pictures).
//   - PDF / text / html -> an <iframe> (Chrome's native PDF viewer,
//                inline text/html rendering).
//   - anything else -> a friendly "no inline preview" panel with a
//                download button.
//
// The content endpoint now defaults to Content-Disposition: inline
// (raindb reserveDirectDownload disposition param), so the browser
// RENDERS instead of force-downloading. The Download button does a
// client-side blob save from the already-loaded bytes, preserving the
// correct filename without a second server round-trip.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TransformWrapper, TransformComponent, } from 'react-zoom-pan-pinch';
import { api } from '../../utils/api.js';
function classifyKind(contentType, filename) {
    const ct = (contentType || '').toLowerCase();
    const name = (filename || '').toLowerCase();
    if (ct.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|avif)$/.test(name)) {
        return 'image';
    }
    if (ct === 'application/pdf' ||
        ct.startsWith('text/') ||
        ct === 'application/json' ||
        ct === 'text/html' ||
        /\.(pdf|txt|md|csv|json|html?)$/.test(name)) {
        return 'frame';
    }
    return 'unknown';
}
export function DocumentPreview({ projectId, documentId, onClose, }) {
    const [meta, setMeta] = useState(null);
    const [err, setErr] = useState(null);
    const [downloading, setDownloading] = useState(false);
    const zoomRef = useRef(null);
    // Fetch document metadata so we can show the filename in the header
    // and pick the right renderer by content-type.
    useEffect(() => {
        let cancelled = false;
        setMeta(null);
        setErr(null);
        (async () => {
            try {
                const r = await api.get(`/p/${projectId}/documents/${documentId}`);
                if (cancelled)
                    return;
                setMeta(r.document);
            }
            catch (e) {
                if (cancelled)
                    return;
                setErr(e instanceof Error ? e.message : 'failed to load document');
            }
        })();
        return () => { cancelled = true; };
    }, [projectId, documentId]);
    // Escape closes the preview. Mounted only while this component is.
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')
                onClose();
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose]);
    // Inline content URL -- the BFF defaults this route to
    // Content-Disposition: inline, so images/PDFs render in-page. iframes
    // and <img> carry cookies (not our Authorization header), and the BFF
    // has a cookie-fallback auth path for exactly this.
    const contentUrl = useMemo(() => `/api/p/${projectId}/documents/${documentId}/content/private`, [projectId, documentId]);
    // Attachment URL -- ?download=1 flips the presign to attachment. Kept
    // as the fallback for the browser-native download path (non-blob).
    const downloadUrl = useMemo(() => `${contentUrl}?download=1`, [contentUrl]);
    const filename = meta?.filename ?? '(loading)';
    const contentType = meta?.contentType ?? '';
    const kind = useMemo(() => classifyKind(contentType, filename), [contentType, filename]);
    // Download from the already-loaded bytes: fetch the inline URL as a
    // blob and save it under the real filename via a temporary <a>. No
    // second server round-trip, correct name, no forced-download presign
    // needed. Falls back to the ?download=1 attachment URL if the blob
    // fetch fails (e.g. opaque cross-origin edge cases).
    const handleDownload = useCallback(async () => {
        setDownloading(true);
        try {
            const resp = await fetch(contentUrl, { credentials: 'include' });
            if (!resp.ok)
                throw new Error(`download failed: ${resp.status}`);
            const blob = await resp.blob();
            const objectUrl = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = objectUrl;
            a.download = meta?.filename || 'download';
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(objectUrl);
        }
        catch {
            // Fallback: let the browser handle the attachment-disposition URL.
            window.location.href = downloadUrl;
        }
        finally {
            setDownloading(false);
        }
    }, [contentUrl, downloadUrl, meta?.filename]);
    return (_jsx("div", { className: "document-preview-overlay", role: "dialog", "aria-modal": "true", "aria-label": `Preview ${filename}`, onClick: (e) => {
            if (e.target === e.currentTarget)
                onClose();
        }, children: _jsxs("div", { className: "document-preview-card", onClick: (e) => e.stopPropagation(), children: [_jsxs("div", { className: "document-preview-header", children: [_jsx("div", { className: "document-preview-title", title: filename, children: filename }), contentType && (_jsx("span", { className: "document-preview-meta", children: contentType })), kind === 'image' && (_jsxs("div", { className: "document-preview-zoom-controls", children: [_jsx("button", { type: "button", className: "document-preview-action", onClick: () => zoomRef.current?.zoomOut(), title: "Zoom out", "aria-label": "Zoom out", children: '\u2212' }), _jsx("button", { type: "button", className: "document-preview-action", onClick: () => zoomRef.current?.zoomIn(), title: "Zoom in", "aria-label": "Zoom in", children: '+' }), _jsx("button", { type: "button", className: "document-preview-action", onClick: () => zoomRef.current?.resetTransform(), title: "Reset zoom", "aria-label": "Reset zoom", children: '\u21ba Fit' })] })), _jsx("button", { type: "button", className: "document-preview-action", onClick: handleDownload, disabled: downloading, title: "Download original", children: downloading ? 'Downloading\u2026' : '\u2913 Download' }), _jsx("button", { type: "button", className: "document-preview-close", onClick: onClose, "aria-label": "Close preview", title: "Close (Esc)", children: '\u2715' })] }), _jsx("div", { className: "document-preview-body", children: err ? (_jsxs("div", { className: "document-preview-error", children: ["Error loading document: ", err] })) : kind === 'image' ? (
                    // Interactive image viewer: fit-to-frame by default, wheel /
                    // pinch to zoom, drag to pan, double-click to zoom, and the
                    // toolbar controls above for zoom in/out/reset.
                    _jsx(TransformWrapper, { ref: zoomRef, centerOnInit: true, minScale: 0.5, maxScale: 8, doubleClick: { mode: 'zoomIn', step: 0.7 }, wheel: { step: 0.15 }, children: _jsx(TransformComponent, { wrapperClass: "document-preview-zoom-wrapper", contentClass: "document-preview-zoom-content", children: _jsx("img", { src: contentUrl, alt: filename, className: "document-preview-image", draggable: false, 
                                // Fit-to-frame the moment the image decodes. centerOnInit
                                // runs before the <img> has intrinsic dimensions, so the
                                // initial centering can be off for large images; resetting
                                // the transform on load recomputes fit + center against the
                                // now-known image size. The CSS (object-fit: contain,
                                // max-* 100%) does the actual scaling; reset aligns the
                                // pan/zoom transform origin to it.
                                onLoad: () => zoomRef.current?.resetTransform(0) }) }) })) : kind === 'frame' ? (
                    // PDF / text / html render natively in the iframe. No
                    // sandbox attribute: the presigned URL is one WE minted and
                    // the bytes load cross-origin from S3 in their own browsing
                    // context. Adding sandbox breaks Chrome's PDF plugin.
                    _jsx("iframe", { className: "document-preview-iframe", src: contentUrl, title: filename })) : (
                    // Unknown/binary type: no inline preview, offer download.
                    _jsxs("div", { className: "document-preview-error", children: [_jsxs("div", { style: { marginBottom: 12 }, children: ["No inline preview for this file type", contentType ? ` (${contentType})` : '', "."] }), _jsx("button", { type: "button", className: "document-preview-action", onClick: handleDownload, disabled: downloading, children: downloading ? 'Downloading\u2026' : '\u2913 Download original' })] })) })] }) }));
}
